import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Badge,
  Button,
  Modal,
  Textarea,
  Select,
  Table,
  Divider,
  Alert,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconEdit,
  IconTrash,
  IconDownload,
  IconFileText,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useProjectSummarySheet,
  useDeleteProjectSummarySheet,
  useSubmitProjectSummarySheet,
  useApprovePeerProjectSummarySheet,
  useApproveHeadProjectSummarySheet,
  useRejectProjectSummarySheet,
  useGenerateProjectSummaryPdf,
  useDownloadProjectSummaryDocument,
} from "../hooks/useProjectSummarySheet";
import { useAuth } from "../../../auth/AuthContext";
import { canEditProjectSummarySheet } from "../permissions";
import { userApi } from "../../../api/users";
import { AmountWithDhivehiWords } from "../../../components/AmountWithDhivehiWords";
import { approvalDisplayName, dhivehiDisplayStyle } from "../../../utils/dhivehiText";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  UNDER_PEER_REVIEW: "yellow",
  UNDER_HOP_REVIEW: "orange",
  APPROVED: "green",
  REJECTED: "red",
};

const stageLabel = (stage: "PEER_CHECK" | "HEAD_OF_PROCUREMENT") =>
  stage === "PEER_CHECK" ? "Peer check" : "Head of Procurement";

export function ProjectSummarySheetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: sheet, isLoading, error } = useProjectSummarySheet(id!);
  const del = useDeleteProjectSummarySheet();
  const submit = useSubmitProjectSummarySheet();
  const approvePeer = useApprovePeerProjectSummarySheet();
  const approveHead = useApproveHeadProjectSummarySheet();
  const reject = useRejectProjectSummarySheet();
  const genPdf = useGenerateProjectSummaryPdf();
  const downloadDoc = useDownloadProjectSummaryDocument();

  const [submitOpen, setSubmitOpen] = useState(false);
  const [checkerId, setCheckerId] = useState<string | null>(null);
  const [officers, setOfficers] = useState<{ value: string; label: string }[]>([]);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [rejectComments, setRejectComments] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    userApi.getUsersForEvaluationReport().then((users) => {
      const opts = users
        .filter(
          (u) => u.role === "PROCUREMENT_OFFICER" && u.id !== user?.id && u.id !== sheet?.submittedById
        )
        .map((u) => ({ value: u.id, label: u.fullName }));
      setOfficers(opts);
    });
  }, [user?.id, sheet?.submittedById]);

  const pendingForUser = useMemo(() => {
    if (!sheet?.approvals || !user?.id) return null;
    return sheet.approvals.find((a) => a.approver.id === user.id && a.status === "PENDING");
  }, [sheet, user?.id]);

  const canPeerApprove =
    sheet?.status === "UNDER_PEER_REVIEW" && pendingForUser?.stage === "PEER_CHECK";
  const canHeadApprove =
    sheet?.status === "UNDER_HOP_REVIEW" &&
    pendingForUser?.stage === "HEAD_OF_PROCUREMENT" &&
    (user?.role === "HEAD_OF_PROCUREMENT" || user?.role === "ADMIN");
  const canReject = !!pendingForUser;
  const canEdit = canEditProjectSummarySheet(sheet, user);
  const isPlaceholderDoc = sheet?.doc?.objectKey?.includes("placeholder");

  const approvals = sheet?.approvals ?? [];
  const rejectionEntries = approvals.filter((a) => a.status === "REJECTED" && a.comments);

  const approvalDateLabel = (a: (typeof approvals)[0]) => {
    if (a.approvedAt) return new Date(a.approvedAt).toLocaleString();
    if (a.rejectedAt) return new Date(a.rejectedAt).toLocaleString();
    return "—";
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading…</Text>
      </Container>
    );
  }

  if (error || !sheet) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load sheet.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>PR-03: {sheet.summarySheetNumber}</Title>
            <Text c="dimmed" size="sm" style={dhivehiDisplayStyle(sheet.projectName)}>
              {sheet.projectName}
            </Text>
          </div>
          <Group>
            <Badge color={statusColors[sheet.status] || "gray"} size="lg">
              {sheet.status.replace(/_/g, " ")}
            </Badge>
            {canEdit && (
              <Button variant="light" leftSection={<IconEdit size={16} />} onClick={() => navigate(`/project-summary-sheets/${sheet.id}/edit`)}>
                Edit
              </Button>
            )}
            <Button variant="light" leftSection={<IconFileText size={16} />} loading={genPdf.isPending} onClick={() => genPdf.mutate(sheet.id)}>
              Download PDF
            </Button>
            {(sheet.status === "DRAFT" || sheet.status === "REJECTED") && (
              <Tooltip label="Delete">
                <ActionIcon color="red" variant="light" onClick={() => setDeleteOpen(true)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        <Card withBorder p="md">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Sheet date
            </Text>
            <Text>{new Date(sheet.sheetDate).toLocaleString()}</Text>
            <Divider />
            <Group grow align="flex-start">
              <Stack gap="xs">
                <Text fw={600}>References</Text>
                <Text size="sm">Bid committee: {sheet.bidCommitteeReference || "—"}</Text>
                <Text size="sm">Agenda: {sheet.agendaReference || "—"}</Text>
                <Text size="sm">Project number: {sheet.projectNumber || "—"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text fw={600}>Funding</Text>
                <Text size="sm" style={dhivehiDisplayStyle(sheet.fundingAgency || undefined)}>
                  {sheet.fundingAgency || "—"}
                </Text>
                <Text size="sm" fw={500}>
                  Estimated
                </Text>
                <AmountWithDhivehiWords amount={sheet.estimatedCost} />
              </Stack>
            </Group>
            <Divider label="Scope" />
            <Text
              size="sm"
              style={{ whiteSpace: "pre-wrap", ...dhivehiDisplayStyle(sheet.scopeOfWorkDetail || undefined) }}
            >
              {sheet.scopeOfWorkDetail || "—"}
            </Text>
            <Divider label="Budget by year" />
            {sheet.budgetAllocations?.length ? (
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Year</Table.Th>
                    <Table.Th>Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sheet.budgetAllocations.map((b) => (
                    <Table.Tr key={b.id}>
                      <Table.Td>{b.budgetYear}</Table.Td>
                      <Table.Td>
                        <AmountWithDhivehiWords amount={b.allocatedAmount} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed">No rows</Text>
            )}
            <Divider label="Recommendation of award" />
            <Group grow align="flex-start">
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Awarded party
                </Text>
                <Text size="sm" style={dhivehiDisplayStyle(sheet.awardedParty || undefined)}>
                  {sheet.awardedParty || "—"}
                </Text>
              </Stack>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Proposed price
                </Text>
                <AmountWithDhivehiWords amount={sheet.proposedPrice} />
              </Stack>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Proposed duration
                </Text>
                <Text size="sm" style={dhivehiDisplayStyle(sheet.proposedDuration || undefined)}>
                  {sheet.proposedDuration || "—"}
                </Text>
              </Stack>
            </Group>
            <Divider label="Details" />
            <Text
              size="sm"
              style={{ whiteSpace: "pre-wrap", ...dhivehiDisplayStyle(sheet.detailsOfTheProject || undefined) }}
            >
              {sheet.detailsOfTheProject || "—"}
            </Text>
            {!isPlaceholderDoc && sheet.doc && (
              <>
                <Divider />
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500} style={{ flex: 1 }} truncate>
                    {sheet.doc.fileName}
                  </Text>
                  <Tooltip label="Download">
                    <ActionIcon
                      variant="light"
                      size="lg"
                      loading={downloadDoc.isPending}
                      onClick={() =>
                        downloadDoc.mutate({ id: sheet.id, fileName: sheet.doc.fileName })
                      }
                      aria-label={`Download ${sheet.doc.fileName}`}
                    >
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {approvals.length > 0 && (
          <Card withBorder p="md">
            <Stack gap="md">
              {sheet.status === "REJECTED" && rejectionEntries.length > 0 && (
                <Alert color="red" title="Rejection reason">
                  <Stack gap="xs">
                    {rejectionEntries.map((a) => (
                      <Text key={a.id} size="sm">
                        <Text span fw={500} style={dhivehiDisplayStyle(approvalDisplayName(a.approver))}>
                          {approvalDisplayName(a.approver)} ({stageLabel(a.stage)}):
                        </Text>{" "}
                        {a.comments}
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Title order={4}>Approval workflow</Title>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Approver</Table.Th>
                    <Table.Th>Stage</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Comments</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {approvals.map((a) => (
                    <Table.Tr key={a.id}>
                      <Table.Td style={dhivehiDisplayStyle(approvalDisplayName(a.approver))}>
                        {approvalDisplayName(a.approver)}
                      </Table.Td>
                      <Table.Td>{stageLabel(a.stage)}</Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            a.status === "APPROVED"
                              ? "green"
                              : a.status === "REJECTED"
                                ? "red"
                                : "gray"
                          }
                        >
                          {a.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{approvalDateLabel(a)}</Table.Td>
                      <Table.Td>
                        {a.status === "REJECTED" && a.comments ? a.comments : "—"}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}

        <Group>
          {canEdit && (
            <Button onClick={() => setSubmitOpen(true)}>Submit for Review</Button>
          )}
          {canPeerApprove && (
            <>
              <Button color="green" leftSection={<IconCheck size={16} />} onClick={() => setApproveOpen(true)}>
                Approve (peer)
              </Button>
              <Button color="red" variant="light" leftSection={<IconX size={16} />} onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            </>
          )}
          {canHeadApprove && (
            <>
              <Button color="green" leftSection={<IconCheck size={16} />} onClick={() => setApproveOpen(true)}>
                Approve (Head of Procurement)
              </Button>
              <Button color="red" variant="light" leftSection={<IconX size={16} />} onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            </>
          )}
        </Group>
      </Stack>

      <Modal opened={submitOpen} onClose={() => setSubmitOpen(false)} title="Select peer checker">
        <Stack>
          <Select label="Procurement officer" data={officers} value={checkerId} onChange={setCheckerId} searchable />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSubmitOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!checkerId}
              loading={submit.isPending}
              onClick={() => {
                if (checkerId) {
                  submit.mutate(
                    { id: sheet.id, checkerId },
                    { onSuccess: () => setSubmitOpen(false) }
                  );
                }
              }}
            >
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={approveOpen} onClose={() => setApproveOpen(false)} title="Approve">
        <Stack>
          <Textarea label="Comments (optional)" value={comments} onChange={(e) => setComments(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={approvePeer.isPending || approveHead.isPending}
              onClick={() => {
                if (canPeerApprove) {
                  approvePeer.mutate(
                    { id: sheet.id, comments: comments.trim() || undefined },
                    { onSuccess: () => { setApproveOpen(false); setComments(""); } }
                  );
                } else {
                  approveHead.mutate(
                    { id: sheet.id, comments: comments.trim() || undefined },
                    { onSuccess: () => { setApproveOpen(false); setComments(""); } }
                  );
                }
              }}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject">
        <Stack>
          <Textarea
            label="Comments (required)"
            required
            value={rejectComments}
            onChange={(e) => setRejectComments(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              disabled={!rejectComments.trim()}
              loading={reject.isPending}
              onClick={() => {
                reject.mutate(
                  { id: sheet.id, comments: rejectComments.trim() },
                  { onSuccess: () => { setRejectOpen(false); setRejectComments(""); } }
                );
              }}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete?">
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={del.isPending}
            onClick={() =>
              del.mutate(sheet.id, {
                onSuccess: () => navigate("/project-summary-sheets"),
              })
            }
          >
            Delete
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
